/**
 * Sprint 3 RTK Query endpoints — Tasks, Meetings, Communications, Reminders.
 * Extends workspaceApi via injectEndpoints (no store.ts changes needed).
 */
import { workspaceApi, type Property, type PropertyAddress } from './workspaceApi';
import { workflowEngineApi } from './workflowEngineApi';
import type { SamPitchBreakdownRow } from '@/components/SAM/samActionTypes';

// Workflow gates (required_note / required_measurement / required_photos / required_document)
// are evaluated server-side on every GET .../transitions call — but that call is cached client-side
// under workflowEngineApi's own `AvailableTransitions` tag, a SEPARATE createApi() instance from
// this file's `workspaceApi`. Creating a note/measurement/media attachment only invalidates THIS
// file's own tags, so a gate that just became satisfied kept showing its old "blocked" message
// until a hard reload happened to land with no stale subscriber still holding the cached result —
// same cross-slice gap as workflowApi.ts's publishWorkflowDefinition bridge. Call this from
// onQueryStarted on any mutation that could satisfy a gate, once its target engagement is known.
function invalidateEngagementGates(dispatch: (action: unknown) => void, engagementId: string | null | undefined) {
  if (!engagementId) return;
  dispatch(workflowEngineApi.util.invalidateTags([
    { type: 'AvailableTransitions', id: engagementId },
  ]));
}

// ─── Response envelope (mirrors workspaceApi) ──────────────────────────────────
interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
}
interface ApiListResponse<T> extends ApiResponse<T[]> {
  total: number;
}

// Detail-by-id envelopes (e.g. a 404) come back with `success: false` and `data: undefined`.
// Some backend endpoints still return that on a 200 status instead of a real 404 (see
// meetings.py / tasks.py _not_found), so `res.data` alone would silently resolve to `undefined`
// and the UI would render a blank card with no error state. Throwing here converts it into a
// proper RTK Query `error`/`isError` instead.
function unwrapDetail<T>(res: ApiResponse<T>): T {
  if (!res.success) throw new Error(res.message || 'Request failed');
  return res.data;
}

// ─── Domain types ──────────────────────────────────────────────────────────────

// Shared
export interface ContextLink {
  context_type: string;
  entity_id: string;
  display_label?: string | null;
}

// ── Properties ──────────────────────────────────────────────────────────────────

export interface PropertyContact {
  name: string;
  role: string;
  is_primary: boolean;
  phone?: string | null;
  email?: string | null;
}

export interface PropertyContactLink {
  property_contact_id: string;
  contact_id: string;
  relationship_type: string;
  is_current: boolean;
  contact: {
    display_name: string;
    phones: { value: string; label?: string | null }[];
    emails: { value: string; label?: string | null }[];
  } | null;
}
export interface PropertyEngagementSummary {
  engagement_id: string;
  title: string;
  engagement_type: string;
  stage: string;
  current_status: string;
  holder_id?: string | null;
  holder_name?: string | null;
  created_on?: string | null;
}
export interface PropertyActiveEngagement {
  engagement_id: string;
  title: string;
  engagement_type: string;
  stage: string;
  status: string;
  /** Total engagements on this property (this one plus however many others) — drives the
   *  "+N more" hint on the list row when a property has more than one. */
  engagement_count: number;
}
export interface PropertyTaskSummary {
  task_id: string;
  title: string;
  status: string;
  priority: string;
  due_date?: string | null;
}
export interface PropertyTimelineEntry {
  date: string;
  label: string;
  description?: string | null;
  engagement_id: string;
}
export interface PropertySummary {
  property_id: string;
  address: PropertyAddress;
  property_type: string;
  owner_name: string;
  contact_ids: string[];
  contacts: PropertyContact[];
  special_instructions?: string | null;
  tags: string[];
  status: string;
  active_engagement?: PropertyActiveEngagement | null;
  // Every engagement on the property — the list card renders one sub-card per entry
  // instead of collapsing extras behind active_engagement's "+N more".
  engagements: PropertyEngagementSummary[];
  display_address?: string;
  // Read-cache surfaced by the backend (updated whenever property_contacts changes).
  // Used by the list card to render "Owner · Photos: N" at-a-glance.
  contact_summary?: {
    current_owner?: PropertyContactSummaryMember | null;
    primary_poc?: PropertyContactSummaryMember | null;
    total_contacts: number;
  } | null;
}
export interface PropertyListParams {
  search?: string;
  /** 'active' narrows to properties with a currently-active engagement; omitted/'all' is unfiltered. */
  engagement_status?: 'active' | 'all';
  /** Free text matching an engagement's current workflow status or stage (e.g. 'Job Scheduled',
   *  'Prospects'), case-insensitive; narrows to properties with a matching engagement. */
  status?: string;
  /** Structured, exact-field filters (case-insensitive) — distinct from `search`'s fuzzy
   *  free-text match across many fields at once. */
  city?: string;
  state?: string;
  postal_code?: string;
  /** Exact match on the property's own type. */
  property_type?: 'residential' | 'commercial' | 'industrial';
  /** The property's OWN active/inactive/sold/archived flag — a DIFFERENT field from `status`
   *  above (which matches an engagement's workflow status/stage). Passing it explicitly
   *  includes archived properties too, overriding the default archived-exclusion. */
  property_status?: string;
  /** Exact match (case-insensitive) on an engagement's engagement_type (e.g. 'Replacement') —
   *  tenant-configurable, never a hardcoded set. */
  engagement_type?: string;
  /** Exact match (case-insensitive) on an engagement's lead-source string (e.g. 'referral'). */
  source?: string;
  /** ISO dates (YYYY-MM-DD), inclusive, filtering on an engagement's created_on. */
  created_after?: string;
  created_before?: string;
  /** ISO dates (YYYY-MM-DD), inclusive, filtering on an engagement's updated_on. */
  updated_after?: string;
  updated_before?: string;
  /** An assignee's name (resolved server-side) or an already-resolved exact user_id. */
  assignee?: string;
  /** Narrows `assignee` to one specific slot instead of all three. */
  assignee_role?: 'sales_agent' | 'project_manager' | 'assigned_user';
  /** A linked contact's name (resolved server-side, same matching as contacts search) or an
   *  already-resolved exact contact_id. */
  contact_name?: string;
  /** Narrows `contact_name` to a specific property-level relationship_type (e.g. 'owner'). */
  contact_role?: string;
  skip?: number;
  limit?: number;
}
// ── Consolidated context (FR-01-011) — Property Profile summaries surfaced on detail ──

export interface PropertyPrimaryContactSummary {
  contact_id: string;
  display_name: string;
  phone?: string | null;
  email?: string | null;
  relationship_type?: string | null;
}
export interface PropertyContactSummaryMember {
  contact_id: string;
  display_name: string;
  phone?: string | null;
  email?: string | null;
}
export interface PropertyContactSummaryBlock {
  current_owner?: PropertyContactSummaryMember | null;
  primary_poc?: PropertyContactSummaryMember | null;
  total_contacts: number;
  refreshed_on?: string | null;
}
export interface PropertyCurrentWarrantySummary {
  warranty_id: string;
  provider: string;
  warranty_type?: string | null;
  coverage_summary?: string | null;
  effective_date?: string | null;
  expiry_date?: string | null;
  status: string;
}
export interface PropertyCurrentMeasurementSummary {
  measurement_id: string;
  how_obtained: string;
  provider?: string | null;
  measured_on?: string | null;
  squares?: number | null;
  total_area_sqft?: number | null;
  pitch?: string | null;
  notes?: string | null;
}
export interface PropertyStructureBlock {
  structure_code?: string | null;
  stories?: number | null;
  hoa_name?: string | null;
  footprint_sqft?: number | null;
  year_built?: number | null;
  notes?: string | null;
}
export interface RoofingInfoBlock {
  roof_type?: string | null;
  material?: string | null;
  manufacturer?: string | null;
  color?: string | null;
  layer_count?: number | null;
  decking_type?: string | null;
  ventilation_type?: string | null;
  chimney_count?: number | null;
  skylight_count?: number | null;
  installed_on?: string | null;
  notes?: string | null;
}

export interface PropertyDetail {
  property_id: string;
  address: PropertyAddress & { latitude?: number | null; longitude?: number | null };
  property_type: string;
  owner_name: string;
  contacts: PropertyContact[];
  special_instructions?: string | null;
  tags: string[];
  status: string;
  active_engagement?: PropertyActiveEngagement | null;
  display_address?: string;
  engagements: PropertyEngagementSummary[];
  tasks: PropertyTaskSummary[];
  timeline: PropertyTimelineEntry[];
  // PCR-FEAT-02 / 03 consolidated context (FR-01-011).
  primary_contact?: PropertyPrimaryContactSummary | null;
  contact_summary?: PropertyContactSummaryBlock | null;
  current_warranty?: PropertyCurrentWarrantySummary | null;
  current_measurement?: PropertyCurrentMeasurementSummary | null;
  property_structure?: PropertyStructureBlock | null;
  roofing_info?: RoofingInfoBlock | null;
}

// ── Property media / warranties / measurements / property-notes lists ────

export interface PropertyMedia {
  media_id: string;
  media_type: string;  // 'photo' | 'video'
  s3_url: string;
  filename?: string | null;
  classifications: string[];
  caption?: string | null;
  taken_on?: string | null;
  engagement_id?: string | null;
}
export interface PropertyWarranty {
  warranty_id: string;
  provider: string;
  warranty_type?: string | null;
  coverage_summary?: string | null;
  effective_date?: string | null;
  expiry_date?: string | null;
  status: string;
  derived_status: string;
  certificate_url?: string | null;
  notes?: string | null;
}
export interface PropertyMeasurement {
  measurement_id: string;
  how_obtained: string;
  provider?: string | null;
  measured_on?: string | null;
  is_current_reference: boolean;
  source_engagement_id?: string | null;
  values: {
    total_area_sqft?: number | null;
    squares?: number | null;
    ridge_length_ft?: number | null;
    hip_length_ft?: number | null;
    valley_length_ft?: number | null;
    eave_length_ft?: number | null;
    rake_length_ft?: number | null;
    pitch?: string | null;
    plane_count?: number | null;
    penetrations?: number | null;
  };
  // FR-03-008 — the report artifact (EagleView PDF, GAF QuickMeasure, etc.) the measurement
  // was derived from. Rendered as a download link on the property card so a contractor can
  // pull up the original for reference during the next assessment.
  report_url?: string | null;
  report_filename?: string | null;
  notes?: string | null;
}
// Powers the "Roof Estimate" section embedded in WorkspacePropertyDetail — coordinates + a
// best-effort AI roof-area estimate for the property's own address (GET .../roof-estimate).
// Same shape as SAM's Quick Quote card payload (SamQuickQuotePayload) minus the address, which
// this endpoint returns fresh (display_line) rather than requiring the caller to already have it.
export interface PropertyRoofEstimate {
  address: string;
  lat: number | null;
  lng: number | null;
  ai_roof_area_m2: number | null;
  ai_roof_area_sqft: number | null;
  ai_squares: number | null;
  ai_estimate_available: boolean;
  // Pitch analysis — identical shape to SamQuickQuotePayload's, so both surfaces share the
  // RoofPitchPanel / TracedMeasurements components. See api-service integrations/google/solar.py.
  ai_ground_area_sqft?: number | null;
  predominant_pitch?: string | null;
  predominant_pitch_degrees?: number | null;
  pitch_multiplier?: number | null;
  pitch_breakdown?: SamPitchBreakdownRow[];
  segment_count?: number;
  imagery_date?: string | null;
  // Ballpark cost — tenant's quick_quote_pricing config × measured area (app.services.pricing.
  // quick_quote). All null when area or pricing isn't available — never a fabricated number.
  pricing_unit?: 'sqft' | 'square' | 'sqm' | null;
  price_per_unit?: number | null;
  complexity_factor?: number | null;
  billable_quantity?: number | null;
  estimated_cost_low?: number | null;
  estimated_cost_high?: number | null;
}
export interface PropertyNoteSummary {
  note_id: string;
  title?: string | null;
  note_type: string;
  source?: string | null;  // 'voice' | 'text' — voice notes carry an audio_url
  audio_url?: string | null;
  final_transcript?: string | null;
  clean_transcript?: string | null;
  review_summary?: string | null;
  created_on?: string | null;
  status: string;
  engagement_id?: string | null;
}

// ── Tasks ───────────────────────────────────────────────────────────────────────

export interface TaskFull {
  task_id: string;
  title: string;
  description: string | null;
  task_type: string;
  priority: string;
  status: string;
  source: string;
  due_date: string | null;
  context: ContextLink | null;
  owner_user_id: string;
  assignee_user_id: string | null;
  queue_role_id: string | null;
  queue_role_name?: string | null;
  follower_user_ids: string[];
  tags: string[];
  deferred_until: string | null;
  active_reminder_count: number;
  created_on: string;
  updated_on: string;
  created_by: string | null;
  is_following: boolean;
  gate_enforced?: boolean;
  permission_denied?: boolean;
  assign_permission_denied?: boolean;
}

export interface TaskActivity {
  activity_type: string;
  actor_user_id: string;
  actor_name: string | null;
  detail: Record<string, unknown> | null;
  occurred_at: string;
}

export interface TaskComment {
  comment_id: string;
  task_id: string;
  content: string;
  author_user_id: string;
  author_name: string | null;
  created_on: string;
  updated_on: string;
}

export interface MyTodoItem {
  item_type: 'task' | 'meeting';
  item_id: string;
  title: string;
  due_on: string | null;
  priority: string | null;
  status: string;
  context_type: string | null;
  context_label: string | null;
  context_entity_id: string | null;
  can_defer: boolean;
  has_reminder: boolean;
  assignee_user_id: string | null;
  // meeting-only
  end_time?: string | null;
  meeting_type?: string | null;
}

export interface RoleQueueItem {
  task_id: string;
  title: string;
  description: string | null;
  priority: string;
  due_on: string | null;
  context_type: string | null;
  context_label: string | null;
  context_entity_id: string | null;
  queue_role_id: string;
  created_on: string;
}

export interface CreateTaskInput {
  title: string;
  description?: string;
  task_type?: string;
  priority?: string;
  source?: string;
  due_date?: string;
  context?: ContextLink;
  assignee_user_id?: string;
  queue_role_id?: string;
  follower_user_ids?: string[];
  tags?: string[];
  idempotency_key?: string;
}

export interface UpdateTaskInput {
  title?: string;
  description?: string;
  task_type?: string;
  priority?: string;
  due_date?: string | null;
  context?: ContextLink;
  tags?: string[];
}

// ── Meetings ────────────────────────────────────────────────────────────────────

export interface ParticipantOut {
  participant_id: string;
  user_id: string | null;
  contact_id: string | null;
  participant_type: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  rsvp_status: string;
}

export interface MeetingFull {
  meeting_id: string;
  title: string;
  description: string | null;
  meeting_type: string;
  status: string;
  start_time: string;
  end_time: string;
  location: Record<string, unknown> | null;
  context: ContextLink | null;
  participants: ParticipantOut[];
  agenda: string | null;
  source_task_id: string | null;
  organizer_user_id: string;
  tags: string[];
  created_on: string;
  updated_on: string;
}

export interface MeetingNote {
  note_id: string;
  meeting_id: string;
  content: string;
  category: string;
  author_user_id: string;
  author_name: string | null;
  created_on: string;
  updated_on: string;
}

export interface MeetingActivity {
  activity_type: string;
  actor_user_id: string;
  actor_name: string | null;
  detail: Record<string, unknown> | null;
  occurred_at: string;
}

export interface CreateMeetingInput {
  title: string;
  confirm_conflicts?: boolean;
  description?: string;
  meeting_type?: string;
  status?: string;
  start_time: string;
  end_time: string;
  location?: { location_type?: string; address?: string; notes?: string; virtual_link?: string };
  context?: ContextLink;
  participants?: Array<{
    user_id?: string;
    contact_id?: string;
    participant_type?: string;
    name?: string;
    email?: string;
    phone?: string;
  }>;
  agenda?: string;
  source_task_id?: string;
  tags?: string[];
}

export interface UpdateMeetingInput {
  title?: string;
  description?: string;
  meeting_type?: string;
  location?: { location_type?: string; address?: string; notes?: string; virtual_link?: string };
  context?: ContextLink;
  agenda?: string;
  tags?: string[];
}

// ── Communications ──────────────────────────────────────────────────────────────

export interface RecipientIn {
  recipient_type: string;
  user_id?: string;
  contact_id?: string;
  name?: string;
  email?: string;
  phone?: string;
}

export type CommunicationChannel = 'email' | 'sms' | 'in_app';
export type CommunicationStatus = 'draft' | 'pending' | 'queued' | 'sent' | 'delivered' | 'failed' | 'discarded' | 'received';

export type CommunicationDirection = 'outbound' | 'inbound';

/** External origin of an inbound reply (COM-FEAT-04). */
export interface CommunicationFromContact {
  name?: string | null;
  recipient_type?: string | null;
  contact_id?: string | null;
  user_id?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface CommunicationAttachment {
  file_name: string;
  url: string;
  size_bytes?: number | null;
  content_type?: string | null;
}

export interface CommunicationRecipient extends RecipientIn {
  status: CommunicationStatus;
  error?: string | null;
  notification_request_id?: string | null;
}

export interface CommunicationOut {
  communication_id: string;
  communication_type: CommunicationChannel;
  direction?: CommunicationDirection;
  from_contact?: CommunicationFromContact | null;
  in_reply_to_communication_id?: string | null;
  sender_user_id: string;
  sender_name?: string | null;
  recipients: CommunicationRecipient[];
  subject?: string | null;
  body: string;
  context?: ContextLink | null;
  status: CommunicationStatus;
  status_timestamps: Record<string, string>;
  error_detail?: string | null;
  attachments: CommunicationAttachment[];
  action_link?: string | null;
  retry_count: number;
  notification_request_ids: string[];
  created_on: string;
  updated_on: string;
}

/** Body for POST /communications/{id}/send — dispatch a persisted SAM draft. All fields are
 *  optional; omitted fields keep the drafted values. */
export interface SendDraftCommunicationInput {
  recipients?: RecipientIn[];
  /** in_app drafts only. */
  recipient_user_ids?: string[];
  subject?: string;
  body?: string;
  context?: ContextLink;
  attachments?: CommunicationAttachment[];
  cc?: RecipientIn[];
  bcc?: RecipientIn[];
  action_link?: string;
}

/** Body for PATCH /communications/{id}/draft — persist in-progress edits WITHOUT sending. Same
 *  partial shape as the send body; omitted fields keep their current values. */
export type UpdateDraftCommunicationInput = SendDraftCommunicationInput;

export interface ComposeEmailInput {
  recipients: RecipientIn[];
  subject: string;
  body: string;
  context?: ContextLink;
  cc?: RecipientIn[];
  bcc?: RecipientIn[];
  attachments?: CommunicationAttachment[];
}

export interface ComposeSmsInput {
  recipients: RecipientIn[];
  body: string;
  context?: ContextLink;
}

export interface ComposeInAppInput {
  recipient_user_ids: string[];
  /** In-app messages have no title/subject — optional and normally omitted. */
  subject?: string;
  body: string;
  context?: ContextLink;
  action_link?: string;
}

// ── Reminders ───────────────────────────────────────────────────────────────────

export interface ReminderOut {
  id: string;
  reminder_type: string;
  title: string;
  message: string | null;
  timezone: string;
  dtstart: string;
  next_fire_on: string | null;
  recurrence_rule: string | null;
  recurrence_end: string | null;
  occurrence_count: number;
  max_occurrences: number | null;
  source_module: string | null;
  source_entity_type: string | null;
  source_entity_id: string | null;
  notification_code: string;
  action: { action_type: string; action_route: string } | null;
  channels_override: string[] | null;
  status: string;
  snooze_count: number;
  last_fired_on: string | null;
  owner_user_id: string;
  created_on: string | null;
  updated_on: string | null;
}

export interface CreateReminderInput {
  reminder_type?: string;
  title: string;
  message?: string;
  timezone?: string;
  dtstart: string;
  recurrence_rule?: string;
  recurrence_end?: string;
  max_occurrences?: number;
  source_module?: string;
  source_entity_type?: string;
  source_entity_id?: string;
  notification_code?: string;
  action?: { action_type: string; action_route: string };
  channels_override?: string[];
}

// ─── Query params ───────────────────────────────────────────────────────────────

interface TaskListParams {
  /** Comma-separated task ids — only these tasks. */
  ids?: string;
  status?: string;
  priority?: string;
  task_type?: string;
  assignee_user_id?: string;
  owner_user_id?: string;
  updated_by_user_id?: string;
  queue_role_id?: string;
  /** Tasks this user follows (appears in the task's follower_user_ids). */
  follower_user_id?: string;
  mine?: boolean;
  my_queues?: boolean;
  context_type?: string;
  context_entity_id?: string;
  due_date_from?: string;
  due_date_to?: string;
  /** Only tasks due STRICTLY before now. A task due later today is NOT overdue — the server owns
   *  that definition now; callers used to approximate it as due_date_to = end of today, which
   *  counted everything due today as already late. */
  overdue?: boolean;
  unassigned?: boolean;
  /** Free-text match on the task TITLE only, composable with every other filter. Distinct from
   *  `useSearchTasksQuery`, which hits /tasks/search — that one also matches description and the
   *  context label, and takes no filters. */
  search?: string;
  /** Scope a workflow checklist to what's still relevant at this status — see api-service's
   *  TaskDAO.list for the exact rule (open tasks always show; only completed/cancelled tasks
   *  tied to a different, already-passed status get hidden). */
  current_workflow_status_code?: string;
  /** Comma-separated = none of these. status/priority are closed enums with no exclude twin —
   *  "not completed" is passed as status="open,cancelled" (every other value) instead. */
  exclude_assignee_user_id?: string;
  created_from?: string;
  created_to?: string;
  /** Only tasks without a due date. */
  no_due_date?: boolean;
  title_starts_with?: string;
  skip?: number;
  limit?: number;
  /** One key or several, comma-separated; a '-' prefix sorts that key descending. */
  sort_by?: string;
  sort_dir?: number;
}

interface MeetingListParams {
  /** Comma-separated meeting ids — only these meetings. */
  ids?: string;
  search?: string;
  status?: string;
  meeting_type?: string;
  organizer_user_id?: string;
  updated_by_user_id?: string;
  /** Paired filter — e.g. context_type: 'engagement', context_entity_id: <engagement_id> — to
   *  find an engagement's own meetings (its assessment meeting, etc.). */
  context_type?: string;
  context_entity_id?: string;
  from_date?: string;
  to_date?: string;
  /** Meeting start-time window (GET /meetings start_from/start_to) — published by SAM's search_meetings. */
  start_from?: string;
  start_to?: string;
  /** Comma-separated = none of these. */
  exclude_status?: string;
  exclude_meeting_type?: string;
  title_starts_with?: string;
  participant_user_id?: string;
  /** Attendee name/email text, CRM contact id(s), minimum attendee count. */
  participant?: string;
  participant_contact_id?: string;
  min_participants?: number;
  /** Only meetings starting from now on / already started. */
  upcoming?: boolean;
  past?: boolean;
  skip?: number;
  limit?: number;
  /** One key or several, comma-separated; a '-' prefix sorts that key descending. */
  sort_by?: string;
  sort_dir?: number;
}

interface CommunicationListParams {
  communication_type?: string;
  status?: string;
  direction?: string;
  skip?: number;
  limit?: number;
}

interface ReminderListParams {
  status?: string;
  source_module?: string;
  source_entity_id?: string;
  from_date?: string;
  to_date?: string;
  skip?: number;
  limit?: number;
}

// ── Weather ─────────────────────────────────────────────────────────────────────
// GET /weather/forecast (app/domain/weather) — job-site hourly forecast, used to show
// conditions for an assessment/site-visit meeting when it's linked to a property with
// resolved coordinates. `job_id` is just a cache key server-side; we pass the property_id
// so repeat meetings at the same address reuse the cached forecast.
export interface WeatherForecastPeriod {
  number: number;
  name: string;
  startTime: string;
  endTime: string;
  isDaytime: boolean;
  temperature: number;
  temperatureUnit: string;
  windSpeed: string;
  windDirection: string;
  shortForecast: string;
  probabilityOfPrecipitation?: { value: number | null } | null;
}
export interface WeatherForecast {
  jobId: string;
  periods: WeatherForecastPeriod[];
}
export interface WeatherForecastParams {
  jobId: string;
  lat: number;
  lon: number;
  siteLabel?: string;
}
export interface CalendarAlertItem {
  type: 'meeting' | 'task';
  title: string;
  time: string;
  property: string;
}
export interface CalendarAlert {
  id: string;
  event: string;
  severity: 'warning' | 'watch' | 'advisory';
  headline: string;
  message: string;
  effective: string;
  expires: string;
  affected_items: CalendarAlertItem[];
  area: string;
}

// ─── Helper ─────────────────────────────────────────────────────────────────────
function toQs(params: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
}

// ─── Injected endpoints ────────────────────────────────────────────────────────

// Client-side fallback only for the (currently unused/no callers) createProperty mutation
// below — the backend's own PropertyAddress.country default ("USA") is the source of truth;
// this just mirrors it so the RTK Query layer doesn't silently assume a country on its own
// without a name attached to the assumption.
const DEFAULT_COUNTRY = 'USA';

const extendedApi = workspaceApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({

    // ── Task queries ──────────────────────────────────────────────────
    getTaskById: builder.query<TaskFull, string>({
      query: (id) => `/api/v1/tasks/${id}`,
      transformResponse: (res: ApiResponse<TaskFull>) => unwrapDetail(res),
      providesTags: (_r, _e, id) => [{ type: 'Task', id }],
    }),
    getTasksList: builder.query<{ data: TaskFull[]; total: number }, TaskListParams | void>({
      query: (p) => `/api/v1/tasks${toQs((p || {}) as Record<string, unknown>)}`,
      transformResponse: (res: ApiListResponse<TaskFull>) => ({ data: res.data, total: res.total }),
      providesTags: [{ type: 'Task', id: 'LIST' }],
    }),
    getMyTodos: builder.query<MyTodoItem[], void>({
      query: () => '/api/v1/tasks/my-todos',
      transformResponse: (res: ApiResponse<MyTodoItem[]>) => res.data,
      providesTags: [{ type: 'Task', id: 'LIST' }, { type: 'Meeting', id: 'LIST' }],
    }),
    searchTasks: builder.query<TaskFull[], string>({
      query: (q) => `/api/v1/tasks/search?q=${encodeURIComponent(q)}`,
      transformResponse: (res: ApiListResponse<TaskFull>) => res.data,
      providesTags: ['Task'],
    }),
    getTaskQueue: builder.query<TaskFull[], string>({
      query: (roleId) => `/api/v1/tasks/queue/${roleId}`,
      transformResponse: (res: ApiListResponse<TaskFull>) => res.data,
      providesTags: ['Task'],
    }),
    getMyRoleQueue: builder.query<RoleQueueItem[], void>({
      query: () => '/api/v1/tasks/my-role-queue',
      transformResponse: (res: ApiListResponse<RoleQueueItem>) => res.data,
      providesTags: ['Task'],
    }),
    getTaskActivity: builder.query<TaskActivity[], string>({
      query: (id) => `/api/v1/tasks/${id}/activity`,
      transformResponse: (res: ApiListResponse<TaskActivity>) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Task', id }],
    }),
    getTaskFollowers: builder.query<string[], string>({
      query: (id) => `/api/v1/tasks/${id}/followers`,
      transformResponse: (res: ApiResponse<string[]>) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Task', id }],
    }),
    getTaskComments: builder.query<TaskComment[], string>({
      query: (id) => `/api/v1/tasks/${id}/comments`,
      transformResponse: (res: ApiListResponse<TaskComment>) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Task', id }],
    }),

    // ── Task mutations ────────────────────────────────────────────────
    createTask: builder.mutation<TaskFull, CreateTaskInput>({
      query: (body) => ({ url: '/api/v1/tasks', method: 'POST', body }),
      transformResponse: (res: ApiResponse<TaskFull>) => res.data,
      invalidatesTags: ['Task', 'DashboardSummary'],
    }),
    updateTask: builder.mutation<TaskFull, { id: string; body: UpdateTaskInput }>({
      query: ({ id, body }) => ({ url: `/api/v1/tasks/${id}`, method: 'PATCH', body }),
      transformResponse: (res: ApiResponse<TaskFull>) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Task', id }, 'Task'],
    }),
    updateTaskStatus: builder.mutation<TaskFull, { id: string; status: string; reason?: string }>({
      query: ({ id, ...body }) => ({ url: `/api/v1/tasks/${id}/status`, method: 'PATCH', body }),
      transformResponse: (res: ApiResponse<TaskFull>) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Task', id }, 'Task', 'DashboardSummary'],
      // Completing/cancelling a task can satisfy a task_completion governance gate (e.g. mandatory
      // job-prep tasks blocking Prepare Job) — same stale-cache gap invalidateEngagementGates
      // already fixes for notes/measurements, just never wired up for task status changes.
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.context?.context_type === 'engagement') invalidateEngagementGates(dispatch, data.context.entity_id);
        } catch { /* update failed */ }
      },
    }),
    assignTask: builder.mutation<TaskFull, { id: string; assignee_user_id?: string; queue_role_id?: string }>({
      query: ({ id, ...body }) => ({ url: `/api/v1/tasks/${id}/assign`, method: 'POST', body }),
      transformResponse: (res: ApiResponse<TaskFull>) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Task', id }, 'Task'],
    }),
    claimTask: builder.mutation<TaskFull, string>({
      query: (id) => ({ url: `/api/v1/tasks/${id}/claim`, method: 'POST' }),
      transformResponse: (res: ApiResponse<TaskFull>) => res.data,
      invalidatesTags: (_r, _e, id) => [{ type: 'Task', id }, 'Task', 'DashboardSummary'],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.context?.context_type === 'engagement') invalidateEngagementGates(dispatch, data.context.entity_id);
        } catch { /* claim failed */ }
      },
    }),
    reassignTask: builder.mutation<TaskFull, { id: string; assignee_user_id?: string; queue_role_id?: string }>({
      query: ({ id, ...body }) => ({ url: `/api/v1/tasks/${id}/reassign`, method: 'POST', body }),
      transformResponse: (res: ApiResponse<TaskFull>) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Task', id }, 'Task'],
    }),
    followTask: builder.mutation<void, string>({
      query: (id) => ({ url: `/api/v1/tasks/${id}/follow`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Task', id }],
    }),
    unfollowTask: builder.mutation<void, string>({
      query: (id) => ({ url: `/api/v1/tasks/${id}/follow`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Task', id }],
    }),
    deferTask: builder.mutation<void, { id: string; defer_until?: string; reason?: string }>({
      query: ({ id, ...body }) => ({ url: `/api/v1/tasks/${id}/defer`, method: 'POST', body }),
      invalidatesTags: ['Task'],
    }),
    addTaskComment: builder.mutation<TaskComment, { taskId: string; content: string }>({
      query: ({ taskId, content }) => ({ url: `/api/v1/tasks/${taskId}/comments`, method: 'POST', body: { content } }),
      transformResponse: (res: ApiResponse<TaskComment>) => res.data,
      invalidatesTags: (_r, _e, { taskId }) => [{ type: 'Task', id: taskId }],
    }),

    // ── Property queries ──────────────────────────────────────────────
    getPropertyDetail: builder.query<PropertyDetail, string>({
      query: (id) => `/api/v1/properties/${id}/detail`,
      transformResponse: (res: ApiResponse<PropertyDetail>) => unwrapDetail(res),
      providesTags: (_r, _e, id) => [{ type: 'Property', id }],
    }),
    getPropertiesList: builder.query<{ data: PropertySummary[]; total: number }, PropertyListParams | void>({
      query: (p) => `/api/v1/properties${toQs((p || {}) as Record<string, unknown>)}`,
      transformResponse: (res: ApiListResponse<PropertySummary>) => ({ data: res.data, total: res.total }),
      providesTags: [{ type: 'Property', id: 'LIST' }],
    }),
    // PCR-FEAT-03 — the four Property Profile sub-lists surfaced inside WorkspacePropertyDetail.
    // Each is a separate query so the detail page can stream in as pieces arrive (no big blocking
    // aggregate call), and each invalidates on its own tag so SAM tool actions (attach media,
    // create measurement, link contact) can refresh just the affected section.
    getPropertyMedia: builder.query<PropertyMedia[], string>({
      query: (id) => `/api/v1/properties/${id}/media`,
      transformResponse: (res: ApiListResponse<PropertyMedia>) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Property', id: `${id}-media` }],
    }),
    getPropertyWarranties: builder.query<PropertyWarranty[], string>({
      query: (id) => `/api/v1/properties/${id}/warranties`,
      transformResponse: (res: ApiListResponse<PropertyWarranty>) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Property', id: `${id}-warranties` }],
    }),
    getPropertyMeasurements: builder.query<PropertyMeasurement[], string>({
      query: (id) => `/api/v1/properties/${id}/measurements`,
      transformResponse: (res: ApiListResponse<PropertyMeasurement>) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Property', id: `${id}-measurements` }],
    }),
    getPropertyNotes: builder.query<PropertyNoteSummary[], string>({
      // status=saved — a note still sitting in draft/ready (Stop Recording creates+analyzes it
      // immediately, before the user reviews and saves) must not show up against the property
      // yet. See notes/NotesList.tsx for the same rule on the standalone Notes list.
      query: (id) => `/api/v1/notes?context_type=property&context_entity_id=${id}&status=saved&limit=50`,
      transformResponse: (res: ApiListResponse<PropertyNoteSummary>) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Property', id: `${id}-notes` }],
    }),
    getPropertyContacts: builder.query<PropertyContactLink[], string>({
      query: (id) => `/api/v1/properties/${id}/contacts`,
      transformResponse: (res: ApiListResponse<PropertyContactLink>) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Property', id: `${id}-contacts` }],
    }),
    linkContactToProperty: builder.mutation<void, { propertyId: string; contact_id: string; relationship_type: string; source_engagement_id?: string }>({
      query: ({ propertyId, ...body }) => ({ url: `/api/v1/properties/${propertyId}/contacts`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { propertyId }) => [{ type: 'Property', id: `${propertyId}-contacts` }, { type: 'Property', id: propertyId }],
    }),
    createContact: builder.mutation<{ contact_id: string }, { first_name?: string; last_name?: string; phones?: Array<{ value: string; is_primary: boolean }>; emails?: Array<{ value: string; is_primary: boolean }> }>({
      query: (body) => ({ url: '/api/v1/contacts', method: 'POST', body: { contact_type: 'individual', ...body } }),
      transformResponse: (res: { data: { contact_id: string } }) => res.data,
      invalidatesTags: ['Contact' as never],
    }),
    getPropertyRoofEstimate: builder.query<PropertyRoofEstimate, string>({
      query: (id) => `/api/v1/properties/${id}/roof-estimate`,
      transformResponse: (res: ApiResponse<PropertyRoofEstimate>) => unwrapDetail(res),
      providesTags: (_r, _e, id) => [{ type: 'Property', id: `${id}-roof-estimate` }],
      // No extended keepUnusedDataFor here — caching lives entirely on the backend now (the
      // roof estimate is persisted on the Property doc itself, see RoofEstimate in
      // api-service/app/domain/properties/models.py), so a repeat request is just a cheap DB
      // read, not a re-run of the Google Solar lookup. Default RTK Query retention applies.
    }),
    // Direct property creation. The backend Google-validates/standardizes the address and
    // returns the existing property instead of erroring if that exact address already exists
    // (see PropertyService.create) — no override flag needed.
    createProperty: builder.mutation<Property, {
      address_street_number: string; address_street_name: string; address_street_suffix: string;
      address_city: string; address_state: string; address_postal_code: string;
      address_pre_direction?: string; address_post_direction?: string;
      address_unit_type?: string; address_unit_number?: string; address_country?: string;
      property_type?: string;
    }>({
      query: (body) => ({
        url: '/api/v1/properties',
        method: 'POST',
        body: {
          property_type: body.property_type || 'residential',
          address: {
            street_number: body.address_street_number, pre_direction: body.address_pre_direction || null,
            street_name: body.address_street_name, street_suffix: body.address_street_suffix,
            post_direction: body.address_post_direction || null,
            unit_type: body.address_unit_type || null, unit_number: body.address_unit_number || null,
            city: body.address_city, state: body.address_state, postal_code: body.address_postal_code,
            country: body.address_country || DEFAULT_COUNTRY,
          },
        },
      }),
      transformResponse: (res: ApiResponse<Property>) => res.data,
      invalidatesTags: ['Property' as never],
    }),
    // Property-profile mutations — invoked by the "+ Add" buttons on the property card.
    // Each invalidates the appropriate list cache AND the property detail (since detail
    // carries the "current_warranty" / "current_measurement" / "contact_summary" summaries).
    uploadMediaFile: builder.mutation<{ url: string; filename: string; content_type: string; size_bytes: number }, FormData>({
      query: (body) => ({ url: '/api/v1/uploads/media', method: 'POST', body }),
      transformResponse: (res: ApiResponse<{ url: string; filename: string; content_type: string; size_bytes: number }>) => res.data,
    }),
    attachPropertyMedia: builder.mutation<PropertyMedia, {
      propertyId: string;
      body: { engagement_id?: string; s3_url: string; media_type: string; filename?: string; classifications?: string[]; caption?: string };
    }>({
      query: ({ propertyId, body }) => ({ url: `/api/v1/properties/${propertyId}/media`, method: 'POST', body: { ...body, property_id: propertyId } }),
      transformResponse: (res: ApiResponse<PropertyMedia>) => res.data,
      invalidatesTags: (_r, _e, { propertyId }) => [{ type: 'Property', id: propertyId }, { type: 'Property', id: `${propertyId}-media` }],
      onQueryStarted: async ({ body }, { dispatch, queryFulfilled }) => {
        try { await queryFulfilled; invalidateEngagementGates(dispatch, body.engagement_id); } catch { /* upload failed */ }
      },
    }),
    // Re-tag (classifications) or delete a single media record — real endpoints
    // (PUT/DELETE /api/v1/media/{id}, see media.py's top-level `router`), used by the
    // assessment capture card's photo gallery (tap a photo to retag or delete).
    updateMedia: builder.mutation<PropertyMedia, { mediaId: string; propertyId: string; body: { classifications?: string[]; caption?: string } }>({
      query: ({ mediaId, body }) => ({ url: `/api/v1/media/${mediaId}`, method: 'PUT', body }),
      transformResponse: (res: ApiResponse<PropertyMedia>) => res.data,
      invalidatesTags: (_r, _e, { propertyId }) => [{ type: 'Property', id: `${propertyId}-media` }],
    }),
    deleteMedia: builder.mutation<void, { mediaId: string; propertyId: string }>({
      query: ({ mediaId }) => ({ url: `/api/v1/media/${mediaId}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { propertyId }) => [{ type: 'Property', id: `${propertyId}-media` }],
    }),
    createPropertyWarranty: builder.mutation<PropertyWarranty, {
      propertyId: string;
      body: { provider: string; warranty_type?: string; coverage_summary?: string; effective_date?: string; expiry_date?: string; source_engagement_id?: string };
    }>({
      query: ({ propertyId, body }) => ({ url: `/api/v1/properties/${propertyId}/warranties`, method: 'POST', body: { ...body, property_id: propertyId } }),
      transformResponse: (res: ApiResponse<PropertyWarranty>) => res.data,
      invalidatesTags: (_r, _e, { propertyId }) => [{ type: 'Property', id: propertyId }, { type: 'Property', id: `${propertyId}-warranties` }],
    }),
    createPropertyMeasurement: builder.mutation<PropertyMeasurement, {
      propertyId: string;
      // `values` is optional — the simple manual-entry form captures a single freeform `notes`
      // string instead of structured dimensions; the backend requires at least one of
      // values/notes/report_url to be present.
      body: { source_engagement_id?: string; how_obtained: string; provider?: string; measured_on?: string; values?: Record<string, number | string | null>; notes?: string; set_as_current_reference?: boolean };
    }>({
      query: ({ propertyId, body }) => ({ url: `/api/v1/properties/${propertyId}/measurements`, method: 'POST', body: { ...body, property_id: propertyId } }),
      transformResponse: (res: ApiResponse<PropertyMeasurement>) => res.data,
      invalidatesTags: (_r, _e, { propertyId }) => [{ type: 'Property', id: propertyId }, { type: 'Property', id: `${propertyId}-measurements` }],
      onQueryStarted: async ({ body }, { dispatch, queryFulfilled }) => {
        try { await queryFulfilled; invalidateEngagementGates(dispatch, body.source_engagement_id); } catch { /* save failed */ }
      },
    }),
    createPropertyNote: builder.mutation<unknown, { propertyId: string; body: { title?: string; text_content: string; note_type: string; engagement_id?: string } }>({
      // Notes attach primarily to the property via `context` (the existing flow) AND now also
      // carry an optional top-level `engagement_id` when captured inside an engagement view,
      // so the engagement's Notes section can filter to just its own notes.
      query: ({ propertyId, body: { title, text_content, note_type, engagement_id } }) => ({
        url: '/api/v1/notes',
        method: 'POST',
        body: {
          title,
          note_type,
          // `content` is the CreateNoteRequest field for the initial text; the server also
          // mirrors it into final_transcript so the note lands in READY status (a note with
          // a transcript is past capture and shows up in the property notes list). Without
          // this, top-level `text_content` was being dropped and the note stayed empty.
          content: text_content,
          final_transcript: text_content,
          source: 'text',
          context: { context_type: 'property', entity_id: propertyId },
          engagement_id,
        },
      }),
      transformResponse: (res: ApiResponse<unknown>) => res.data,
      invalidatesTags: (_r, _e, { propertyId }) => [{ type: 'Property', id: `${propertyId}-notes` }],
      onQueryStarted: async ({ body }, { dispatch, queryFulfilled }) => {
        try { await queryFulfilled; invalidateEngagementGates(dispatch, body.engagement_id); } catch { /* save failed */ }
      },
    }),

    // ── Meeting queries ───────────────────────────────────────────────
    getMeetingById: builder.query<MeetingFull, string>({
      query: (id) => `/api/v1/meetings/${id}`,
      transformResponse: (res: ApiResponse<MeetingFull>) => unwrapDetail(res),
      providesTags: (_r, _e, id) => [{ type: 'Meeting', id }],
    }),
    getMeetingsList: builder.query<{ data: MeetingFull[]; total: number }, MeetingListParams | void>({
      query: (p) => `/api/v1/meetings${toQs((p || {}) as Record<string, unknown>)}`,
      transformResponse: (res: ApiListResponse<MeetingFull>) => ({ data: res.data, total: res.total }),
      providesTags: [{ type: 'Meeting', id: 'LIST' }],
    }),
    getMeetingActivity: builder.query<MeetingActivity[], string>({
      query: (id) => `/api/v1/meetings/${id}/activity`,
      transformResponse: (res: ApiListResponse<MeetingActivity>) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Meeting', id }],
    }),
    getMeetingNotes: builder.query<MeetingNote[], string>({
      query: (id) => `/api/v1/meetings/${id}/notes`,
      transformResponse: (res: ApiListResponse<MeetingNote>) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Meeting', id }],
    }),
    checkMeetingConflicts: builder.query<MeetingFull[], { user_ids: string[]; start_time: string; end_time: string; exclude_meeting_id?: string }>({
      query: (p) => `/api/v1/meetings/conflicts${toQs(p as unknown as Record<string, unknown>)}`,
      transformResponse: (res: ApiListResponse<MeetingFull>) => res.data,
    }),
    getCalendarMeetings: builder.query<MeetingFull[], { start_from: string; start_to: string }>({
      query: (p) => `/api/v1/meetings/calendar${toQs(p as unknown as Record<string, unknown>)}`,
      transformResponse: (res: ApiListResponse<MeetingFull>) => res.data,
      providesTags: ['Meeting', 'CalendarEvent'],
    }),
    getWeatherForecast: builder.query<WeatherForecast, WeatherForecastParams>({
      query: ({ jobId, lat, lon, siteLabel }) =>
        `/api/v1/weather/forecast${toQs({ job_id: jobId, lat, lon, site_label: siteLabel })}`,
      transformResponse: (res: ApiResponse<WeatherForecast>) => res.data,
    }),
    getCalendarAlerts: builder.query<CalendarAlert[], { from: string; to: string }>({
      query: (p) => `/api/v1/weather/calendar-alerts${toQs(p as unknown as Record<string, unknown>)}`,
      transformResponse: (res: ApiListResponse<CalendarAlert>) => res.data,
    }),

    // ── Meeting mutations ─────────────────────────────────────────────
    createMeeting: builder.mutation<MeetingFull, CreateMeetingInput>({
      query: (body) => ({ url: '/api/v1/meetings', method: 'POST', body }),
      transformResponse: (res: ApiResponse<MeetingFull>) => res.data,
      invalidatesTags: ['Meeting', 'CalendarEvent', 'DashboardSummary'],
    }),
    updateMeeting: builder.mutation<MeetingFull, { id: string; body: UpdateMeetingInput }>({
      query: ({ id, body }) => ({ url: `/api/v1/meetings/${id}`, method: 'PATCH', body }),
      transformResponse: (res: ApiResponse<MeetingFull>) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Meeting', id }, 'Meeting', 'CalendarEvent'],
    }),
    rescheduleMeeting: builder.mutation<MeetingFull, { id: string; start_time: string; end_time: string; reason?: string; notify_participants?: boolean; confirm_conflicts?: boolean }>({
      query: ({ id, ...body }) => ({ url: `/api/v1/meetings/${id}/reschedule`, method: 'POST', body }),
      transformResponse: (res: ApiResponse<MeetingFull>) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Meeting', id }, 'Meeting', 'CalendarEvent'],
    }),
    cancelMeeting: builder.mutation<MeetingFull, { id: string; reason?: string; notify_participants?: boolean }>({
      query: ({ id, ...body }) => ({ url: `/api/v1/meetings/${id}/cancel`, method: 'POST', body }),
      transformResponse: (res: ApiResponse<MeetingFull>) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Meeting', id }, 'Meeting', 'CalendarEvent', 'DashboardSummary'],
    }),
    updateMeetingStatus: builder.mutation<MeetingFull, { id: string; status: string; reason?: string }>({
      query: ({ id, ...body }) => ({ url: `/api/v1/meetings/${id}/status`, method: 'PATCH', body }),
      transformResponse: (res: ApiResponse<MeetingFull>) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Meeting', id }, 'Meeting'],
    }),
    addMeetingParticipants: builder.mutation<MeetingFull, { id: string; participants: Array<{ user_id?: string; contact_id?: string; participant_type?: string; name?: string; email?: string; phone?: string }>; confirm_conflicts?: boolean }>({
      query: ({ id, participants, confirm_conflicts }) => ({ url: `/api/v1/meetings/${id}/participants`, method: 'POST', body: { participants, confirm_conflicts } }),
      transformResponse: (res: ApiResponse<MeetingFull>) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Meeting', id }],
    }),
    removeMeetingParticipant: builder.mutation<void, { meetingId: string; participantId: string }>({
      query: ({ meetingId, participantId }) => ({ url: `/api/v1/meetings/${meetingId}/participants/${participantId}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { meetingId }) => [{ type: 'Meeting', id: meetingId }],
    }),
    sendMeetingInvitations: builder.mutation<void, string>({
      query: (id) => ({ url: `/api/v1/meetings/${id}/invite`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Meeting', id }],
    }),
    updateMeetingRsvp: builder.mutation<MeetingFull, { meetingId: string; participantId: string; rsvp_status: string }>({
      query: ({ meetingId, participantId, rsvp_status }) => ({ url: `/api/v1/meetings/${meetingId}/participants/${participantId}/rsvp`, method: 'PATCH', body: { rsvp_status } }),
      transformResponse: (res: ApiResponse<MeetingFull>) => res.data,
      invalidatesTags: (_r, _e, { meetingId }) => [{ type: 'Meeting', id: meetingId }],
    }),
    addMeetingNote: builder.mutation<MeetingNote, { meetingId: string; content: string; category?: string }>({
      query: ({ meetingId, ...body }) => ({ url: `/api/v1/meetings/${meetingId}/notes`, method: 'POST', body }),
      transformResponse: (res: ApiResponse<MeetingNote>) => res.data,
      invalidatesTags: (_r, _e, { meetingId }) => [{ type: 'Meeting', id: meetingId }],
    }),
    updateMeetingNote: builder.mutation<MeetingNote, { meetingId: string; noteId: string; content?: string; category?: string }>({
      query: ({ meetingId, noteId, ...body }) => ({ url: `/api/v1/meetings/${meetingId}/notes/${noteId}`, method: 'PATCH', body }),
      transformResponse: (res: ApiResponse<MeetingNote>) => res.data,
      invalidatesTags: (_r, _e, { meetingId }) => [{ type: 'Meeting', id: meetingId }],
    }),
    deleteMeetingNote: builder.mutation<void, { meetingId: string; noteId: string }>({
      query: ({ meetingId, noteId }) => ({ url: `/api/v1/meetings/${meetingId}/notes/${noteId}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { meetingId }) => [{ type: 'Meeting', id: meetingId }],
    }),
    convertMeetingNote: builder.mutation<unknown, { meetingId: string; noteId: string; target_type: string; additional_data?: Record<string, unknown> }>({
      query: ({ meetingId, noteId, ...body }) => ({ url: `/api/v1/meetings/${meetingId}/notes/${noteId}/convert`, method: 'POST', body }),
      transformResponse: (res: ApiResponse<unknown>) => res.data,
      invalidatesTags: (_r, _e, { meetingId }) => [{ type: 'Meeting', id: meetingId }, 'Task'],
    }),

    // ── Communication queries ─────────────────────────────────────────
    getCommunications: builder.query<{ data: CommunicationOut[]; total: number }, CommunicationListParams | void>({
      query: (p) => `/api/v1/communications${toQs((p || {}) as Record<string, unknown>)}`,
      transformResponse: (res: ApiListResponse<CommunicationOut>) => ({ data: res.data, total: res.total }),
      providesTags: ['Communication' as never],
    }),
    getCommunicationById: builder.query<CommunicationOut, string>({
      query: (id) => `/api/v1/communications/${id}`,
      transformResponse: (res: ApiResponse<CommunicationOut>) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Communication' as never, id }],
    }),
    getCommunicationsByContext: builder.query<{ data: CommunicationOut[]; total: number }, { contextType: string; entityId: string; skip?: number; limit?: number }>({
      query: ({ contextType, entityId, ...rest }) => `/api/v1/communications/context/${contextType}/${entityId}${toQs(rest as Record<string, unknown>)}`,
      transformResponse: (res: ApiListResponse<CommunicationOut>) => ({ data: res.data, total: res.total }),
      providesTags: ['Communication' as never],
    }),

    // ── Communication mutations ───────────────────────────────────────
    sendEmail: builder.mutation<CommunicationOut, ComposeEmailInput>({
      query: (body) => ({ url: '/api/v1/communications/email', method: 'POST', body }),
      transformResponse: (res: ApiResponse<CommunicationOut>) => res.data,
      invalidatesTags: ['Communication' as never],
    }),
    sendSms: builder.mutation<CommunicationOut, ComposeSmsInput>({
      query: (body) => ({ url: '/api/v1/communications/sms', method: 'POST', body }),
      transformResponse: (res: ApiResponse<CommunicationOut>) => res.data,
      invalidatesTags: ['Communication' as never],
    }),
    sendInAppMessage: builder.mutation<CommunicationOut, ComposeInAppInput>({
      query: (body) => ({ url: '/api/v1/communications/in-app', method: 'POST', body }),
      transformResponse: (res: ApiResponse<CommunicationOut>) => res.data,
      invalidatesTags: ['Communication' as never],
    }),
    retryCommunication: builder.mutation<CommunicationOut, string>({
      query: (id) => ({ url: `/api/v1/communications/${id}/retry`, method: 'POST' }),
      transformResponse: (res: ApiResponse<CommunicationOut>) => res.data,
      invalidatesTags: ['Communication' as never],
    }),
    sendDraftCommunication: builder.mutation<CommunicationOut, { id: string; body: SendDraftCommunicationInput }>({
      query: ({ id, body }) => ({ url: `/api/v1/communications/${id}/send`, method: 'POST', body }),
      transformResponse: (res: ApiResponse<CommunicationOut>) => res.data,
      invalidatesTags: ['Communication' as never],
    }),
    // Live draft edit — persist the user's in-progress changes WITHOUT sending. No tag invalidation:
    // the ComposeCard holds the authoritative in-flight edits, and a refetch mid-typing would clobber
    // them (SAM live-edits re-seed via the DRAFT_COMMUNICATION rev bump, not a cache invalidation).
    updateDraftCommunication: builder.mutation<CommunicationOut, { id: string; body: UpdateDraftCommunicationInput }>({
      query: ({ id, body }) => ({ url: `/api/v1/communications/${id}/draft`, method: 'PATCH', body }),
      transformResponse: (res: ApiResponse<CommunicationOut>) => res.data,
    }),
    discardDraftCommunication: builder.mutation<CommunicationOut, string>({
      query: (id) => ({ url: `/api/v1/communications/${id}/discard`, method: 'POST' }),
      transformResponse: (res: ApiResponse<CommunicationOut>) => res.data,
      invalidatesTags: ['Communication' as never],
    }),
    uploadCommunicationAttachment: builder.mutation<CommunicationAttachment, File>({
      query: (file) => {
        const form = new FormData();
        form.append('file', file);
        return { url: '/api/v1/communications/attachments', method: 'POST', body: form };
      },
      transformResponse: (res: ApiResponse<CommunicationAttachment>) => res.data,
    }),

    // ── Reminder queries ──────────────────────────────────────────────
    getReminders: builder.query<{ data: ReminderOut[]; total: number }, ReminderListParams | void>({
      query: (p) => `/api/v1/reminders${toQs((p || {}) as Record<string, unknown>)}`,
      transformResponse: (res: ApiListResponse<ReminderOut>) => ({ data: res.data, total: res.total }),
      providesTags: ['Reminder' as never],
    }),
    getReminderById: builder.query<ReminderOut, string>({
      query: (id) => `/api/v1/reminders/${id}`,
      transformResponse: (res: ApiResponse<ReminderOut>) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Reminder' as never, id }],
    }),

    // ── Reminder mutations ────────────────────────────────────────────
    createReminder: builder.mutation<ReminderOut, CreateReminderInput>({
      query: (body) => ({ url: '/api/v1/reminders', method: 'POST', body }),
      transformResponse: (res: ApiResponse<ReminderOut>) => res.data,
      invalidatesTags: ['Reminder' as never],
    }),
    updateReminder: builder.mutation<ReminderOut, { id: string; body: Partial<CreateReminderInput> }>({
      query: ({ id, body }) => ({ url: `/api/v1/reminders/${id}`, method: 'PATCH', body }),
      transformResponse: (res: ApiResponse<ReminderOut>) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Reminder' as never, id }, 'Reminder' as never],
    }),
    cancelReminder: builder.mutation<void, string>({
      query: (id) => ({ url: `/api/v1/reminders/${id}/cancel`, method: 'POST' }),
      invalidatesTags: ['Reminder' as never],
    }),
    snoozeReminder: builder.mutation<ReminderOut, { id: string; duration?: string; until?: string }>({
      query: ({ id, ...body }) => ({ url: `/api/v1/reminders/${id}/snooze`, method: 'POST', body }),
      transformResponse: (res: ApiResponse<ReminderOut>) => res.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Reminder' as never, id }, 'Reminder' as never],
    }),
  }),
});

export const {
  // Tasks
  useGetTaskByIdQuery,
  useGetTasksListQuery,
  useGetMyTodosQuery,
  useSearchTasksQuery,
  useLazySearchTasksQuery,
  useGetTaskQueueQuery,
  useGetMyRoleQueueQuery,
  useGetTaskActivityQuery,
  useGetTaskFollowersQuery,
  useGetTaskCommentsQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useUpdateTaskStatusMutation,
  useAssignTaskMutation,
  useClaimTaskMutation,
  useReassignTaskMutation,
  useFollowTaskMutation,
  useUnfollowTaskMutation,
  useDeferTaskMutation,
  useAddTaskCommentMutation,
  // Properties
  useGetPropertyDetailQuery,
  useGetPropertiesListQuery,
  useGetPropertyMediaQuery,
  useGetPropertyWarrantiesQuery,
  useGetPropertyMeasurementsQuery,
  useGetPropertyNotesQuery,
  useGetPropertyContactsQuery,
  useLinkContactToPropertyMutation,
  useCreateContactMutation,
  useGetPropertyRoofEstimateQuery,
  useLazyGetPropertyRoofEstimateQuery,
  useCreatePropertyMutation,
  useUploadMediaFileMutation,
  useAttachPropertyMediaMutation,
  useUpdateMediaMutation,
  useDeleteMediaMutation,
  useCreatePropertyWarrantyMutation,
  useCreatePropertyMeasurementMutation,
  useCreatePropertyNoteMutation,
  // Meetings
  useGetMeetingByIdQuery,
  useGetMeetingsListQuery,
  useGetMeetingActivityQuery,
  useGetMeetingNotesQuery,
  useCheckMeetingConflictsQuery,
  useGetCalendarMeetingsQuery,
  useGetWeatherForecastQuery,
  useGetCalendarAlertsQuery,
  useCreateMeetingMutation,
  useUpdateMeetingMutation,
  useRescheduleMeetingMutation,
  useCancelMeetingMutation,
  useUpdateMeetingStatusMutation,
  useAddMeetingParticipantsMutation,
  useRemoveMeetingParticipantMutation,
  useSendMeetingInvitationsMutation,
  useUpdateMeetingRsvpMutation,
  useAddMeetingNoteMutation,
  useUpdateMeetingNoteMutation,
  useDeleteMeetingNoteMutation,
  useConvertMeetingNoteMutation,
  // Communications
  useGetCommunicationsQuery,
  useGetCommunicationByIdQuery,
  useGetCommunicationsByContextQuery,
  useSendEmailMutation,
  useSendSmsMutation,
  useSendInAppMessageMutation,
  useRetryCommunicationMutation,
  useSendDraftCommunicationMutation,
  useUpdateDraftCommunicationMutation,
  useDiscardDraftCommunicationMutation,
  useUploadCommunicationAttachmentMutation,
  // Reminders
  useGetRemindersQuery,
  useGetReminderByIdQuery,
  useCreateReminderMutation,
  useUpdateReminderMutation,
  useCancelReminderMutation,
  useSnoozeReminderMutation,
} = extendedApi;
